import React, { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import "./maintable.css";
import { CgPushRight } from "react-icons/cg";
import { taskApi } from "api/tasks/taskApi";
import { current } from "@reduxjs/toolkit";
import { FaCalendarAlt, FaSyncAlt } from "react-icons/fa";
import { useAlert } from "components/UI_components/Alert";
import CalendarModal from "./CalendarModal";
import TaskEmptyIcon from "assets/empty state/task.svg";
import Xarrow from "react-xarrows";
import { sub } from "date-fns";
import { IoMdCalendar } from "react-icons/io";
/**
 * MAIN-TABLE VIEW
 * ------------------------------------------------------------------
 * All presentation logic for the "Main Table" tab lives here.  The
 * component is completely stateless; everything it needs arrives via
 * props so TaskHome stays lean.
 */


const priorityToNumeric = {
  High: 3,
  Medium: 2,
  Low: 1,
  NotSet: 0,
};
// Shared colors so status / priority / duration feel consistent
// Slightly stronger than the pill backgrounds, but same hue family
// Tailwind red-300 plus custom upcoming yellow used elsewhere
const OVERDUE_COLOR = "#ffdbdb"; // medium red, aligned with High/Overdue
const COMPLETED_DURATION_COLOR = "#e5e0ff"; // soft purple for completed duration
const UPCOMING_DURATION_COLOR = "#fcffbb"; // upcoming duration / "Complete in 0D" color
const EARLY_COMPLETE_COLOR = "#c1ffe5"; // green when completed before planned end

// Match Duration pill text to the same "tinted background + strong text" style
// as Priority badges (High/Medium/Low).
const mapDurationTextClass = (item) => {
  const now = new Date();
  const endDate = new Date(item.end_date);
  const msInDay = 1000 * 60 * 60 * 24;

  const isCompleted =
    item.status === "Completed" ||
    item.status === "Complete" ||
    item.is_completed;

  if (isCompleted) {
    // "Before" uses green, completed date range uses purple
    return endDate > now ? "text-green-800" : "text-purple-800";
  }

  const diffMs = now.getTime() - endDate.getTime();
  // Upcoming (including "due today" window) uses yellow
  if (endDate > now || (diffMs > 0 && diffMs < msInDay)) return "text-yellow-800";

  // Overdue uses red
  return "text-red-800";
};

const mapBackground = (task) => {
  const now = new Date();
  const endDate = new Date(task.end_date);
  const msInDay = 1000 * 60 * 60 * 24;

  // Completed tasks / subtasks
  if (task.status === "Completed" || task.status === "Complete" || task.is_completed) {
    // Completed before the planned end date – keep green
    if (endDate > now) return EARLY_COMPLETE_COLOR;
    // Completed on / after planned end – use softer color instead of dark blue
    return COMPLETED_DURATION_COLOR;
  }

  // Not completed yet
  // Treat "due today" (less than 1 day past end date) as upcoming, not overdue,
  // so "Complete in 0D" uses the upcoming duration color instead of red.
  const diffMs = now.getTime() - endDate.getTime();
  if (endDate > now || (diffMs > 0 && diffMs < msInDay)) {
    return UPCOMING_DURATION_COLOR;
  }

  // Overdue (pending) – only after at least 1 full day
  return OVERDUE_COLOR;
};
const renderTextForDue = (task) => {
  const now = new Date();
  const endDate = new Date(task.end_date);
  const msInDay = 1000 * 60 * 60 * 24;

  if (task.status === "Completed" || task.status === "Complete") {
    if (endDate > now) return "Before";

    const start = new Date(task.start_date);
    const end = new Date(task.end_date);
    return `${start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })} - ${end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  }

  // Pending tasks
  if (endDate > now) {
    return "Complete in";
  }

  // Avoid showing "Overdue By 0D" – only mark overdue after a full day
  const isOverdueByAtLeastOneDay = now.getTime() - endDate.getTime() >= msInDay;
  return isOverdueByAtLeastOneDay ? "Overdue By" : "Complete in";
};

const renderSubtaskTextForDue = (subtask) => {
  const now = new Date();
  const endDate = new Date(subtask.end_date);
  const msInDay = 1000 * 60 * 60 * 24;

  if (subtask.is_completed) {
    if (endDate > now) return "Before";

    const start = new Date(subtask.start_date);
    const end = subtask.completed_on
      ? new Date(subtask.completed_on)
      : new Date(subtask.end_date);
    return `${start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })} - ${end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  }

  // Pending subtasks
  if (endDate > now) {
    return "Complete in";
  }

  // Avoid "Overdue By 0D" on the creation / due date – only after 1 full day
  const isOverdueByAtLeastOneDay = now.getTime() - endDate.getTime() >= msInDay;
  return isOverdueByAtLeastOneDay ? "Overdue By" : "Complete in";
};

const renderSubtaskDays = (subtask) => {
  const now = new Date();
  const currentTime = now.getTime();
  const endTime = new Date(subtask.end_date).getTime();
  const startTime = new Date(subtask.start_date).getTime();
  const completedTime = subtask.completed_on ? new Date(subtask.completed_on).getTime() : new Date(subtask.end_date).getTime();
  const msInDay = 1000 * 60 * 60 * 24;
  // Check for invalid dates
  if (isNaN(endTime) || isNaN(startTime) || isNaN(currentTime)) {
    return 0;
  }
  if (subtask.is_completed) {
    if (endTime > currentTime) {
      const days = (endTime - currentTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // how many days before it ended
    } else {
      const days = (completedTime - startTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // duration of subtask
    }
  } else {
    if (endTime < currentTime) {
      const days = (currentTime - endTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // overdue by days
    } else {
      const days = (endTime - currentTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // days left to complete
    }
  }
};

const renderDays = (task) => {
  const now = new Date();
  const currentTime = now.getTime();
  const endTime = new Date(task.end_date).getTime();
  const startTime = new Date(task.start_date).getTime();
  const msInDay = 1000 * 60 * 60 * 24;

  // Check for invalid dates
  if (isNaN(endTime) || isNaN(startTime) || isNaN(currentTime)) {
    return 0;
  }

  if (task.status === "Completed" || task.status === "Complete") {
    if (endTime > currentTime) {
      const days = (endTime - currentTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // how many days before it ended
    } else {
      const days = (endTime - startTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // duration of task
    }
  } else {
    if (endTime < currentTime) {
      const days = (currentTime - endTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // overdue by days
    } else {
      const days = (endTime - currentTime) / msInDay;
      return days < 1 ? 0 : Math.ceil(days); // days left to complete
    }
  }
};

const MainTableView = ({
  onRefresh,
  refreshing,
  /* data */
  taskGroups,
  refetchTasks,
  refetchSubtasks,
  mergeGroupAllocatedMembers,

  /* high-level UI actions */
  onAddTaskGroupClick,
  onAddTaskClick,
  onAddSubtaskClick,
  handleAddTask,
  showAddTaskModal,
  setShowAddTaskModal,
  showAddSubtaskModal,
  setShowAddSubtaskModal,
  handleAddSubtask,

  /* expand / collapse */
  onToggleGroup,
  onToggleTask,
  onToggleSubComplete,

  /* status / priority */
  onTaskStatusChange,
  onTaskPriorityChange,
  statusOptions,
  openStatusDropdown,
  setOpenStatusDropdown,
  openPriorityDropdown,
  setOpenPriorityDropdown,

  /* helpers */
  getInitials,
  getAvatarColor,
  getStatusProportions,

  /* inline-edit (group) */
  editingGroupId,
  editingGroupName,
  onGroupEditStart,
  onGroupEditCancel,
  onGroupEditSubmit,

  /* inline-edit (task) */
  editingTaskId,
  editingTaskName,
  onTaskEditStart,
  onTaskEditCancel,
  onTaskEditSubmit,
  handleTaskDateChange,
  handleSubtaskDateChange,

  /* inline-edit (subtask) */
  editingSubId,
  editingSubName,
  onSubEditStart,
  onSubEditCancel,
  onSubEditSubmit,

  /* misc */
  deleteSubtask,
  selectedTaskGroup,
  selectedTask,
  selectedSubtask,
  setSelectedTaskGroup,
  setSelectedTask,
  setSelectedSubtask,
}) => {
  const [siteMembers, setSiteMembers] = useState([]);
  const [showAddTaskMemberModal, setShowAddTaskMemberModal] = useState(null);
  const [showAddSubtaskMemberModal, setShowAddSubtaskMemberModal] =
    useState(null);
  const { addAlert } = useAlert();
  const [taskInputValue, setTaskInputValue] = useState("");
  const [subtaskInputValue, setSubtaskInputValue] = useState("");
  const [isTaskCalendarOpen, setIsTaskCalendarOpen] = useState(null);
  const [isSubtaskCalendarOpen, setIsSubtaskCalendarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'Ongoing', 'Completed', 'Overdue'
  const [priorityFilter, setPriorityFilter] = useState("all"); // 'all', 'High', 'Medium', 'Low'
  const [showFilters, setShowFilters] = useState(false);
  const [currentUserMember, setCurrentUserMember] = useState(null);
  const [taskAuthority, setTaskAuthority] = useState(false);
  const [openStatusFilterDropdown, setOpenStatusFilterDropdown] =
    useState(false);
  const [openPriorityFilterDropdown, setOpenPriorityFilterDropdown] =
    useState(false);
  const isOwner = localStorage.getItem("isOwner") === "true";
  // const [openPriorityDropdown, setOpenPriorityDropdown] = useState(null);

  /* ─────────────── bulk select mode (per group) ─────────────── */
  const [bulkEditGroupId, setBulkEditGroupId] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkMembers, setBulkMembers] = useState([]);
  const [bulkPriority, setBulkPriority] = useState("");
  const [bulkDelete, setBulkDelete] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showBulkAssigneeDropdown, setShowBulkAssigneeDropdown] = useState(false);

  const toggleTaskSelected = (taskId) =>
    setSelectedTaskIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );

  const toggleBulkMemberSelection = (memberId) => {
    setBulkMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleBulkSave = async (tg) => {
    try {
      if (!selectedTaskIds.length) {
        addAlert("Select at least one task.", "warning");
        return;
      }
      if (
        !bulkDelete &&
        !bulkMembers.length &&
        !bulkPriority
      ) {
        addAlert("Choose at least one bulk action.", "warning");
        return;
      }
      setBulkSaving(true);
      const ops = [];
      for (const taskId of selectedTaskIds) {
        if (bulkDelete) {
          ops.push(taskApi.deleteTask(taskId));
          continue; // if deleting, skip other updates
        }
        const updates = {};
        if (bulkPriority) {
          updates.priority = priorityToNumeric[bulkPriority];
        }
        if (Object.keys(updates).length) {
          ops.push(taskApi.updateTask(taskId, updates));
        }
        if (bulkMembers.length) {
          const taskRef = tg.tasks.find((t) => t.id === taskId);
          const existingMemberIds =
            (taskRef?.members || []).map((m) => m.member_id) || [];
          const toAdd = bulkMembers.filter(
            (mId) => !existingMemberIds.includes(mId)
          );
          if (toAdd.length) {
            ops.push(
              taskApi.addTaskMembers({
                task_id: taskId,
                member_ids: toAdd,
              })
            );
          }
        }
      }
      await Promise.allSettled(ops);
      addAlert("Bulk changes applied.", "success");
      await refetchTasks(tg.id);
    } catch (e) {
      console.error("Bulk action failed:", e);
      addAlert("Failed to apply bulk changes.", "error");
    } finally {
      setBulkSaving(false);
      setBulkEditGroupId(null);
      setSelectedTaskIds([]);
      setBulkMembers([]);
      setBulkPriority("");
      setBulkDelete(false);
    }
  };

  // Filter function to check if a task matches search term, status, and priority
  const matchesFilters = (task) => {
    // Search by task name or subtask names
    const searchMatches =
      searchTerm === "" ||
      task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.subtasks?.some((st) =>
        st.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

    // Status filter
    const statusMatches =
      statusFilter === "all" || task.status === statusFilter;

    // Priority filter
    const priorityMatches =
      priorityFilter === "all" || task.priority === priorityFilter;

    return searchMatches && statusMatches && priorityMatches;
  };

  // Filter task groups based on their tasks
  const filterTaskGroups = (groups) => {
    return groups.map((group) => ({
      ...group,
      tasks: group.tasks.filter((task) => {
        // Show task if it matches OR it has subtasks that match
        const taskMatches = matchesFilters(task);
        const hasMatchingSubtasks =
          task.subtasks?.some((st) => matchesFilters(st)) || false;
        return taskMatches || hasMatchingSubtasks;
      }),
    })); // Only show groups with matching tasks
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openStatusFilterDropdown) setOpenStatusFilterDropdown(false);
      if (openPriorityFilterDropdown) setOpenPriorityFilterDropdown(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openStatusFilterDropdown, openPriorityFilterDropdown]);

  const fetchSiteMembers = async () => {
    try {
      const siteData = JSON.parse(localStorage.getItem("siteData"));
      const response = await taskApi.getSiteMembers(siteData.id);
      if (response.status) {
        setSiteMembers(response.data);
        const user = JSON.parse(localStorage.getItem("user"));
        const currentMember = response.data.find(
          (member) => member.user_id === user.user
        );
        setCurrentUserMember(currentMember);
        setTaskAuthority(
          currentMember?.create_taskgroup ||
          currentMember?.is_admin ||
          currentMember?.joined_as === "Owner"
        );
      } else {
        console.error("Failed to fetch site members:", response.message);
      }
    } catch (error) {
      console.error("Error fetching site members:", error);
    }
  };
  useEffect(() => {
    fetchSiteMembers();
  }, []);

  // Close status/priority dropdowns on outside click
  useEffect(() => {
    const handleGlobalClick = () => {
      if (openStatusDropdown !== null) setOpenStatusDropdown(null);
      if (openPriorityDropdown !== null) setOpenPriorityDropdown(null);
      if (showBulkAssigneeDropdown) setShowBulkAssigneeDropdown(false);
    };
    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [
    openStatusDropdown,
    openPriorityDropdown,
    setOpenStatusDropdown,
    setOpenPriorityDropdown,
    showBulkAssigneeDropdown,
  ]);

  /* ────────────────── sub components ────────────────── */

  const AddTaskMemberSubModal = ({ task, onClose }) => {
    const [selectedMembers, setSelectedMembers] = useState([]);
    // const [searchTerm, setSearchTerm] = useState("");
    const [filteredMembers, setFilteredMembers] = useState(
      siteMembers?.filter(
        (member) => !task.members.some((m) => m.member_id === member.member_id)
      ) || []
    );
    const [taskMembers, setTaskMembers] = useState(task.members || []);

    const handleMemberSelect = (member) => {
      if (selectedMembers.some((m) => m.member_id === member.member_id)) {
        setSelectedMembers(
          selectedMembers.filter((m) => m.member_id !== member.member_id)
        );
      } else {
        setSelectedMembers([...selectedMembers, member]);
      }
    };

    const handleAddMembers = async () => {
      const newMembers = selectedMembers.filter(
        (m) => !taskMembers.some((tm) => tm.member_id === m.member_id)
      );
      if (newMembers.length > 0) {
        try {
          const response = await taskApi.addTaskMembers({
            task_id: task.id,
            member_ids: newMembers.map((m) => m.member_id),
          });

          const data = await response.json();

          if (data.detail) {
            addAlert(
              "You do not have permission to add members to task. You must have Task Authority Permission",
              "error"
            );
          }

          // setTaskMembers([...taskMembers, ...newMembers]);
          setSelectedMembers([]);
          // console.log("selected task group:", selectedTaskGroup);
          refetchTasks(task.task_group);
          // Immediately reflect in group's avatars without full refresh
          mergeGroupAllocatedMembers?.(task.task_group, newMembers);

          onClose();
        } catch (error) {
          console.error("Error adding task members:", error);
        }
      } else {
        console.warn("No new members selected to add.");
      }
    };

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-96">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Add Task Members</h2>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={onClose}
            >
              ✕
            </button>
          </div>

          <div className="max-h-60 overflow-y-auto mb-4">
            {filteredMembers.length > 0 ? (
              filteredMembers.map((member) => (
                <div
                  key={member.member_id}
                  className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-gray-100 ${selectedMembers.some(
                    (m) => m.member_id === member.member_id
                  )
                    ? "bg-gray-200"
                    : ""
                    }`}
                  onClick={() => handleMemberSelect(member)}
                >
                  <div className="flex items-center">
                    {member?.userpic ? (
                      <img
                        src={member.userpic}
                        alt={`${member.first_name} ${member.last_name}`}
                        className="w-8 h-8 rounded-full border-2 border-white mr-2"
                      />
                    ) : (
                      <span
                        className="w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm flex items-center justify-center mr-2"
                        title={`${member.first_name} ${member.last_name}`}
                      >
                        {getInitials(
                          `${member.first_name} ${member.last_name}`
                        )}
                      </span>
                    )}
                    <span className="text-sm text-gray-700">
                      {member.first_name} {member.last_name}
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedMembers.some(
                      (m) => m.member_id === member.member_id
                    )}
                    onChange={() => handleMemberSelect(member)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">
                No members found to add.
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              onClick={handleAddMembers}
            >
              Add Members
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AddSubTaskMemberModal = ({ task, subtask, onClose }) => {
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Function to get initials from name
    const getInitials = (name) => {
      const names = name.split(" ").filter(Boolean);
      return names.length > 0
        ? names
          .slice(0, 2)
          .map((n) => n[0]?.toUpperCase())
          .join("")
        : "";
    };

    const fetchTaskMembers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await taskApi.getTaskMembers(task.id);
        if (response.status) {
          setFilteredMembers(response.data);
        } else {
          console.error("Failed to fetch task members:", response.message);
          setError("Failed to fetch task members.");
        }
      } catch (error) {
        console.error("Error fetching task members:", error);
        setError("Error fetching task members.");
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchTaskMembers();
    }, [task.id]);

    useEffect(() => {
      // Initialize selectedMembers with current subtask assignees
      const initialSelected = (subtask.assignee || []).map((assignee) => {
        const taskMember = filteredMembers.find(
          (m) => m.id === assignee.member_id
        );
        return {
          ...assignee,
          member_id: assignee.member_id, // Maps to task member's id
        };
      });
      setSelectedMembers(initialSelected);
    }, [subtask.assignee, filteredMembers]);

    const handleMemberSelect = (member) => {
      if (selectedMembers.some((m) => m.member_id === member.id)) {
        setSelectedMembers(
          selectedMembers.filter((m) => m.member_id !== member.id)
        );
      } else {
        const memberWithId = {
          ...member,
          member_id: member.id, // Use task member's id
        };
        setSelectedMembers([...selectedMembers, memberWithId]);
      }
    };

    const handleAddMembers = async () => {
      if (selectedMembers.length === 0) {
        console.warn("No members selected to add.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await taskApi.updateSubtask(subtask.id, {
          assign_to: selectedMembers.map((m) => m.member_id), // Send task member's id
        });
        // const data = await response.json();
        if (response.detail) {
          addAlert(
            "You do not have permission to add members to subtask. You must have Task Authority Permission",
            "error"
          );
          setSelectedMembers([]);
          refetchSubtasks(task.task_group, task.id);
          onClose();
          return;
        }

        if (response.status) {
          setSelectedMembers([]);
          refetchSubtasks(task.task_group, task.id);
          // Merge the selected members into group avatars for instant feedback
          mergeGroupAllocatedMembers?.(task.task_group, selectedMembers);
          onClose();
        } else {
          console.error("Failed to update subtask members:", response.message);
          setError("Failed to update subtask members.");
        }
      } catch (error) {
        console.error("Error updating subtask members:", error);
        setError("Error updating subtask members.");
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-96">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Add Subtask Members</h2>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={onClose}
              disabled={loading}
            >
              ✕
            </button>
          </div>
          {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
          <div className="max-h-60 overflow-y-auto mb-4">
            {loading ? (
              <div className="text-gray-500 text-sm">Loading members...</div>
            ) : filteredMembers.length > 0 ? (
              filteredMembers.map((member) => (
                <div
                  key={member.id}
                  className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-gray-100 ${selectedMembers.some((m) => m.member_id === member.id)
                    ? "bg-gray-200"
                    : ""
                    }`}
                  onClick={() => handleMemberSelect(member)}
                >
                  <div className="flex items-center">
                    {member?.userpic ? (
                      <img
                        src={member.userpic}
                        alt={`${member.first_name} ${member.last_name}`}
                        className="w-8 h-8 rounded-full border-2 border-white mr-2"
                      />
                    ) : (
                      <span
                        className="w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm flex items-center justify-center mr-2"
                        title={`${member.first_name} ${member.last_name}`}
                      >
                        {getInitials(
                          `${member.first_name} ${member.last_name}`
                        )}
                      </span>
                    )}
                    <span className="text-sm text-gray-700">
                      {member.first_name} {member.last_name}
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedMembers.some(
                      (m) => m.member_id === member.id
                    )}
                    onChange={() => handleMemberSelect(member)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">
                No members available to add.
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-btn-bg text-white rounded hover:bg-purple-800"
              onClick={handleAddMembers}
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Members"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const StatusBadge = ({ value }) => {
    const colorMapping = {
      Ongoing: "#F0B41D",
      Complete: "#00C875",
      Completed: "#00C875",
      Overdue: OVERDUE_COLOR,
      Halted: OVERDUE_COLOR,
      High: OVERDUE_COLOR,
      Medium: "#F0B41D",
      Low: "#00C875",
    };
    const cls =
      value === "Ongoing" || value === "Medium"
        ? " text-yellow-800 bg-yellow-100 "
        : value === "Completed" || value === "Complete" || value === "Low"
          ? "  text-green-800 bg-green-100"
          : value === "Overdue" || value === "High" || value === "Halted"
            ? " text-red-800 bg-red-100"
            : " text-gray-800 bg-gray-100";
    return (
      <span
        className={clsx(
          `{px-4 py-1 rounded-md items-center w-full font-normal text-sm flex flex-row justify-center hover:bg-gray-200 text-white}`,
          cls
        )}
        style={{
          gap: "0.5rem",
        }}
      >
        {/* <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colorMapping[value] || "#78797A" }}
        /> */}
        {value}
      </span>
    );
  };

  const PriorityBadge = ({ value }) => (
    <span
      className={clsx(
        "px-6 py-1 rounded-md text-sm inline-flex items-center w-full font-normal flex-row justify-center",
        value === "High"
          ? "bg-red-100 text-red-800 hover:bg-red-200"
          : value === "Medium"
            ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
            : "bg-green-100 text-green-800 hover:bg-green-200"
      )}
      style={{
        gap: "0.5rem",
      }}
    >
      <span
        className={clsx(
          "w-2 h-2 rounded-full",
          value === "High"
            ? "bg-red-500"
            : value === "Medium"
              ? "bg-yellow-500"
              : "bg-green-500"
        )}
      />
      {value}
    </span>
  );

  const StatusFilterDropdown = () => (
    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border z-30 w-48">
      <div className="px-4 py-2 text-sm font-medium text-gray-700 border-b">
        Filter by Status
      </div>
      {["all", "Ongoing", "Completed", "Overdue"].map((status) => (
        <button
          key={status}
          className={clsx(
            "w-full text-left px-4 py-2 text-sm hover:bg-gray-100",
            statusFilter === status && "bg-purple-100 text-purple-700"
          )}
          onClick={() => {
            setStatusFilter(status);
            setOpenStatusFilterDropdown(false);
          }}
        >
          {status === "all" ? "All Statuses" : status}
        </button>
      ))}
    </div>
  );

  const PriorityFilterDropdown = () => (
    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border z-30 w-48">
      <div className="px-4 py-2 text-sm font-medium text-gray-700 border-b">
        Filter by Priority
      </div>
      {["all", "High", "Medium", "Low"].map((priority) => (
        <button
          key={priority}
          className={clsx(
            "w-full text-left px-4 py-2 text-sm hover:bg-gray-100",
            priorityFilter === priority && "bg-purple-100 text-purple-700"
          )}
          onClick={() => {
            setPriorityFilter(priority);
            setOpenPriorityFilterDropdown(false);
          }}
        >
          {priority === "all" ? "All Priorities" : priority}
        </button>
      ))}
    </div>
  );



  const FilterButton = () => {
    const filterRef = useRef(null);

    useEffect(() => {
      const handleOutsideClick = (e) => {
        if (!filterRef.current) return;
        if (!filterRef.current.contains(e.target)) {
          setShowFilters(false);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    return (
      <div className="relative" ref={filterRef}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span>Filters</span>
          {(statusFilter !== "all" || priorityFilter !== "all") && (
            <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
          )}
        </button>

        {showFilters && (
          <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border p-4 w-80 z-30">
            <div className="space-y-3">
              {/* Status Filter */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenStatusFilterDropdown(!openStatusFilterDropdown);
                  }}
                  className="w-full flex justify-between items-center p-2 border rounded hover:bg-gray-50"
                >
                  <span>
                    {statusFilter === "all" ? "All Statuses" : statusFilter}
                  </span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openStatusFilterDropdown && <StatusFilterDropdown />}
              </div>

              {/* Priority Filter */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPriorityFilterDropdown(!openPriorityFilterDropdown);
                  }}
                  className="w-full flex justify-between items-center p-2 border rounded hover:bg-gray-50"
                >
                  <span>
                    {priorityFilter === "all" ? "All Priorities" : priorityFilter}
                  </span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openPriorityFilterDropdown && <PriorityFilterDropdown />}
              </div>

              {/* Clear Filters */}
              {(statusFilter !== "all" || priorityFilter !== "all") && (
                <button
                  onClick={() => {
                    setStatusFilter("all");
                    setPriorityFilter("all");
                  }}
                  className="w-full p-2 text-sm text-red-600 hover:bg-red-50 rounded"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );

  }; // end FilterButton

  /* -------- SUBTASK ROW -------- */
  const SubtaskRow = ({ tg, task, st, isOdd }) => {
    const [tempSubtaskName, setTempSubtaskName] = useState(st.name);
    return (
      <>
        <Xarrow
          start={`task-${task.id}`}
          end={`subtask-${st.id}`}
          startAnchor="bottom"
          endAnchor="left"
          strokeWidth={2}
          color="#3B0069"
          zIndex={0}
          path="grid"
        />
        <tr
          className="py-1 group hover:bg-[#ECE9EE] cursor-pointer col-span-8"
          style={{
            // borderTop: "1px solid #78797A",
            borderBottom: "1px solid #78797A",
          }}
          id={`subtask-${st.id}`}
        >
          {/* name + checkbox */}
          <td
            className="pl-5 pr-1 py-2 text-left w-[200px] text-sm text-gray-700 "
            onClick={(e) => {
              e.stopPropagation();
              setSelectedSubtask({ ...st, task_id: task.id });
              setSelectedTaskGroup(null);
              setSelectedTask(null);
            }}
          >
            <div className="flex flex-row items-center justify-between w-full">
              <div className="flex items-center w-[85%]">
                <input
                  type="checkbox"
                  className="mr-2 text-purple-600"
                  style={{
                    accentColor: "#3B0069",
                  }}
                  checked={st.status === "Completed"}
                  height={20}
                  width={20}
                />


                {/* inline-edit name */}
                {editingSubId === st.id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSubEditSubmit(tempSubtaskName);
                    }}
                    className="flex items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      autoFocus
                      value={tempSubtaskName}
                      maxLength={100}
                      onChange={(e) =>
                        setTempSubtaskName(e.target.value)
                      }
                      onBlur={(e) => {
                        e.preventDefault();
                        onSubEditSubmit(tempSubtaskName);
                      }}
                      className="pl-2 w-full text-[14px] mr-2 border border-purple-300 rounded"
                    />
                    <button
                      type="button"
                      onClick={onSubEditCancel}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                  </form>
                ) : (
                  <span
                    className={`truncate cursor-pointer text-wrap flex px-1 rounded-lg flex-wrap hover:text-purple-600 ${taskAuthority
                      ? "hover:outline hover:outline-1 hover:outline-black"
                      : ""
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (taskAuthority) {
                        onSubEditStart(st);
                      } else {
                        setSelectedSubtask({ ...st, task_id: task.id });
                        setSelectedTaskGroup(null);
                        setSelectedTask(null);
                      }
                    }}
                  >
                    {st.name}
                  </span>
                )}
              </div>
              <button
                className="ml-2 p-0.5 hover:bg-gray-200 rounded-full text-gray-600 "
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSubtask({ ...st, task_id: task.id });
                  setSelectedTaskGroup(null);
                  setSelectedTask(null);
                }}
                title="Details"
              >
                <CgPushRight
                  size={18}
                  color="black"
                  className="hidden group-hover:block"
                />
              </button>
            </div>
          </td>

          {/* assignee avatars */}
          <td className="pl-3 pr-1 py-2 text-center w-[170px]">
            <div
              className="flex flex-row items-center justify-start w-full px-3"
              onClick={() => {
                showAddSubtaskMemberModal === st.id
                  ? setShowAddSubtaskMemberModal(null)
                  : setShowAddSubtaskMemberModal(st.id);
              }}
            >
              {Array.isArray(st.assignee) && st.assignee.length ? (
                <>
                  {st.assignee.slice(0, 3).map((m) =>
                    m?.userpic === "" ? (
                      <span
                        key={m.member_id}
                        className="flex flex-row items-center justify-center w-8 h-8 bg-[#343C6A] rounded-full text-white font-medium text-sm border-2 border-white"
                        title={`${m.first_name} ${m.last_name}`}
                      >
                        {getInitials(`${m.first_name} ${m.last_name}`)}
                      </span>
                    ) : (
                      <img
                        key={m.member_id}
                        src={m?.userpic}
                        alt={`${m.first_name} ${m.last_name}`}
                        className="w-8 h-8 rounded-full border-2 border-white"
                      />
                    )
                  )}
                  {st.assignee.length > 3 && (
                    <span
                      className="flex flex-row items-center justify-center w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm border-2 border-white"
                      title={`+${st.assignee.length - 3} more members`}
                    >
                      +{st.assignee.length - 3}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">Unassigned</span>
              )}
            </div>
            {showAddSubtaskMemberModal === st.id && (
              <AddSubTaskMemberModal
                task={task}
                subtask={st}
                onClose={() => setShowAddSubtaskMemberModal(null)}
              />
            )}
          </td>

          {/* due date */}
          {/* <td className="px-4 py-2 text-center w-[140px] ">{st.dueDate}</td> */}
          <td
            className="px-1 py-2 text-center w-[130px] relative"
            onClick={(e) => {
              e.stopPropagation(); // Add this line
              setIsSubtaskCalendarOpen(st.id);
            }}
          >
            {st.dueDate ? st.dueDate : (
              <div className="flex flex-row items-center justify-center w-full">
                {/* <IoMdCalendar size={20} color="#3b0069" /> */}
                <span className="text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded-md hover:bg-purple-200">
                  Select date
                </span>
              </div>
            )}
            <CalendarModal
              isVisible={isSubtaskCalendarOpen === st.id}
              startDate={st.start_date}
              endDate={st.end_date}
              onClose={() => {
                setIsSubtaskCalendarOpen(null);
              }}
              // key={task.id}
              onDateChange={(start, end) => {
                handleSubtaskDateChange(st.id, start, end);
                setIsSubtaskCalendarOpen(null);
              }}
            />
          </td>

          {/* status */}
          <td className="px-2 py-2 text-center w-[130px] ">
            <div className="w-full px-2 mx-auto">
              {st.dueDate && (
                <StatusBadge value={st.status} />
              )}
            </div>
          </td>

          {/* priority */}
          <td className="px-4 py-2 text-center w-[130px]  ">
            {/* <PriorityBadge value={st.priority} /> */}
            {st.is_completed && (
              <div className="flex flex-col items-center justify-center w-full px-2">
                <span className="text-sm font-normal">Completed on</span>
                <span className="text-xs font-medium">
                  {st.completed_on || st.end_date}
                </span>
              </div>
            )}
          </td>

          <td className="px-2 py-2 text-center w-[130px] ">
            {st.dueDate && (
              <div
                className={clsx(
                  "rounded-md px-3 py-1 justify-end flex flex-row items-center font-medium text-xs",
                  mapDurationTextClass(st)
                )}
                style={{
                  gap: "5px",
                  background: mapBackground(st),
                }}
              >
                {renderSubtaskTextForDue(st)}
                <div className="rounded-full bg-white p-1 text-[#343C6A] font-semibold">
                  {renderSubtaskDays(st)}D
                </div>
              </div>
            )}
          </td>
        </tr>
      </>
    )
  };
  /* -------- TASK ROW + subtasks in a separate table -------- */
  const TaskBlock = ({ tg, task, idx }) => {
    const [tempTaskName, setTempTaskName] = useState(task.name);
    return (
      <>
        <tr
          className="hover:bg-[#ECE9EE] border-b-[1px] z-50 border-[#78797A] cursor-pointer"
          style={{ borderLeft: "6px solid #3B0069" }}
        >
          {/* bulk select checkbox */}
          {bulkEditGroupId === tg.id && (
            <td className="px-0 py-2 w-[40px] text-center">
              <input
                type="checkbox"
                className="w-4 h-4 accent-[#3B0069]"
                checked={selectedTaskIds.includes(task.id)}
                onChange={() => toggleTaskSelected(task.id)}
                onClick={(e) => e.stopPropagation()}
              />
            </td>
          )}
          {/* task name + expand arrow */}
          <td
            className={`pl-${bulkEditGroupId === tg.id ? "0" : "4"} pr-1 py-0 z-30 w-[300px] font-normal group`}
            onClick={() => {
              setSelectedTask(task);
              setSelectedTaskGroup(null);
              setSelectedSubtask(null);
            }}
            id={`task-row-${task.id}`}
          >
            <div className="flex items-center w-full">
              <button
                className="mr-1 expand-arrow rounded-full p-[0.5px] hover:bg-gray-300"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleTask(e, tg.id, task.id);
                }}
                id={`task-${task.id}`}
              >
                <svg
                  className={clsx("w-4 h-4 transform", {
                    "rotate-90": task.expanded,
                  })}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M9 5l7 7-7 7" strokeWidth={2} strokeLinecap="round" />
                </svg>
              </button>

              {/* inline-edit task name */}
              <div className="flex flex-row items-center justify-between w-[76%]">
                {editingTaskId === task.id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onTaskEditSubmit(tempTaskName)
                    }}
                    className="flex items-center w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      autoFocus
                      value={tempTaskName}
                      maxLength={45}
                      onChange={(e) => {
                        setTempTaskName(e.target.value)
                        // onTaskEditStart({ ...task, name: e.target.value })
                      }}
                      onBlur={(e) => {
                        e.preventDefault();
                        onTaskEditSubmit(tempTaskName)
                      }}
                      className="pl-2 text-[16px] mr-2 border border-purple-300 rounded"
                    />
                    <button
                      type="button"
                      onClick={onTaskEditCancel}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                  </form>
                ) : (
                  <div className="cursor-pointer flex flex-col text-wrap">
                    <div className="inline-flex items-center">
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (taskAuthority) {
                            onTaskEditStart(task);
                          } else {
                            setSelectedTask(task);
                            setSelectedTaskGroup(null);
                            setSelectedSubtask(null);
                          }
                        }}
                        className={`truncate text-wrap flex-wrap px-1 rounded-md hover:text-purple-600 ${taskAuthority
                          ? "hover:outline hover:outline-1 hover:outline-black"
                          : ""
                          }`}
                      >
                        {task.name}
                        {/* a round icon which shows number of subtasks */}
                        {task?.subtasks_summary?.total_subtasks > 0 && !task.expanded && (
                          <span className="rounded-full ml-2 bg-purple-200 text-[12px] py-0.5 px-2 text-[#3B0069] font-medium">
                            {task.subtasks_summary.total_subtasks}
                          </span>
                        )}
                      </span>
                    </div>
                    {task.expanded &&
                      Array.isArray(task.subtasks) &&
                      task.subtasks.length > 0 ? (
                      <div className="text-xs text-gray-500 whitespace-nowrap text-left">
                        {
                          task.subtasks.filter(
                            (st) =>
                              st.status === "Complete" ||
                              st.status === "Completed"
                          ).length
                        }
                        /{task.subtasks.length} subtasks complete
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              {/* info button */}
              <button
                className="p-1 hover:bg-gray-200 w-8 rounded-full ml-2 hidden group-hover:block"
                onClick={(e) => {
                  setSelectedTask(task);
                  setSelectedTaskGroup(null);
                }}
              >
                <CgPushRight size={22} color="black" />
              </button>
            </div>
          </td>
          {/* assignee */}
          <td className="px-2 py-2 text-center w-[170px]">
            <div
              className="flex flex-row items-center justify-start w-full px-[20px]"
              onClick={() => {
                showAddTaskMemberModal === task.id
                  ? setShowAddTaskMemberModal(null)
                  : setShowAddTaskMemberModal(task.id);
              }}
            >
              {Array.isArray(task.members) && task.members.length ? (
                <>
                  {task.members.slice(0, 3).map((m) =>
                    siteMembers.find((sm) => sm.member_id === m.member_id)
                      ?.userpic === "" ? (
                      <span
                        key={m.member_id}
                        className="flex flex-row items-center justify-center w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm border-2 border-white"
                        title={`${m.first_name} ${m.last_name}`}
                      >
                        {getInitials(`${m.first_name} ${m.last_name}`)}
                      </span>
                    ) : (
                      <img
                        key={m.member_id}
                        src={
                          siteMembers.find((sm) => sm.member_id === m.member_id)?.userpic
                        }
                        alt={`${m.first_name} ${m.last_name}`}
                        className="w-8 h-8 rounded-full border-2 border-white"
                        title={`${m.first_name} ${m.last_name}`}
                      />
                    )
                  )}
                  {task.members.length > 3 && (
                    <span
                      className="flex flex-row items-center justify-center w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-sm border-2 border-white"
                      title={`+${task.members.length - 3} more members`}
                    >
                      +{task.members.length - 3}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">Unassigned</span>
              )}
            </div>
            {showAddTaskMemberModal === task.id && (
              <AddTaskMemberSubModal
                task={task}
                onClose={() => setShowAddTaskMemberModal(null)}
              />
            )}
          </td>

          {/* due date */}
          <td
            className="px-2 py-2 text-center w-[150px] relative"
            onClick={(e) => {
              e.stopPropagation(); // Add this line
              setIsTaskCalendarOpen(task.id);
            }}
          >
            {task.dueDate ? task.dueDate : (
              <div className="flex flex-row items-center justify-center w-full">
                {/* <IoMdCalendar size={20} color="#3b0069" /> */}
                <span className="text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded-md hover:bg-purple-200">
                  Select date
                </span>
              </div>
            )}
            <CalendarModal
              isVisible={isTaskCalendarOpen === task.id}
              startDate={task.start_date}
              endDate={task.end_date}
              onClose={() => {
                setIsTaskCalendarOpen(null);
                // console.log("Calendar closed for task:", task.id);
              }}
              // key={task.id}
              onDateChange={(start, end) => {
                // console.log("Task date changed:", start, end);
                handleTaskDateChange(task.id, start, end);
                setIsTaskCalendarOpen(null);
              }}
            />
          </td>

          {/* status dropdown */}
          <td className="relative px-2 py-2 w-[150px]">
            <button
              type="button"
              onMouseDown={(e) => {
                // Prevent focus-driven scroll jumps on some layouts/browsers.
                // Priority uses a <div> (no focus), but Status is a <button>.
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Preserve window scroll when opening/closing the dropdown.
                // This protects against full-page jumps when DOM changes.
                const y = window.scrollY;
                setOpenStatusDropdown(
                  openStatusDropdown === task.id ? null : task.id
                );
                requestAnimationFrame(() => {
                  window.scrollTo({ top: y, left: 0, behavior: "auto" });
                });
              }}
              className="w-full px-1"
            >
              {task?.dueDate && (
                <StatusBadge value={task.status} />
              )}
            </button>

            {openStatusDropdown === task.id && (
              <div
                className="absolute top-full px-2 py-2 shadow-xl shadow-[#00000040] left-1/2 -translate-x-1/2 z-20 w-48 bg-white rounded-xl border flex flex-col mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center text-gray-700 font-semibold py-2 border-b-[1px] border-[#78797A] mb-2">
                  Change status
                </div>
                {statusOptions.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    className={clsx("text-lg font-semibold mb-2 ")}
                    onMouseDown={(e) => {
                      // Same reason as the toggle: don't let focus change scroll the window.
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskStatusChange(tg.id, task.id, opt.label);
                      setOpenStatusDropdown(null);
                    }}
                  >
                    <StatusBadge value={opt.label} />
                  </button>
                ))}
              </div>
            )}
          </td>


          {/* priority dropdown */}
          <td className="relative py-2   w-[150px]">
            <div
              className="w-full px-2  "
              style={{
                alignSelf: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setOpenPriorityDropdown(
                  openPriorityDropdown === task.id ? null : task.id
                );
              }}
            >
              <PriorityBadge value={task.priority} />
            </div>
            {openPriorityDropdown === task.id && (
              <div
                className="absolute top-full px-2 py-2 shadow-xl shadow-[#00000040] left-1/2 -translate-x-1/2 z-20 w-48 bg-white rounded-xl border flex flex-col mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center text-gray-700 font-semibold py-2 border-b-[1px] border-[#78797A] mb-2">
                  Change priority
                </div>
                {["High", "Medium", "Low"].map((opt) => (
                  <button
                    key={opt}
                    className="text-lg font-semibold mb-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskPriorityChange(
                        tg.id,
                        task.id,
                        priorityToNumeric[opt]
                      );
                      setOpenPriorityDropdown(null);
                    }}
                  >
                    <StatusBadge value={opt} />
                  </button>
                ))}
              </div>
            )}
          </td>

          <td className="px-2 py-2 text-center w-[150px]">
            {task?.dueDate && (
              <div
                className={clsx(
                  "rounded-md px-3 py-1 justify-end flex flex-row items-center font-medium text-xs",
                  mapDurationTextClass(task)
                )}
                style={{
                  gap: "5px",
                  background: mapBackground(task),
                }}
              >
                {renderTextForDue(task)}
                <div className="rounded-full bg-white p-1 text-[#343C6A] font-semibold">
                  {renderDays(task)}D
                </div>
              </div>
            )}
          </td>
        </tr>
        {/* Subtasks in a separate table */}
        {
          task.expanded && (
            <tr>
              <td
                colSpan={bulkEditGroupId === tg.id ? 7 : 6}
                className="p-0  bg-[#F0F2F5] border-l-[6px] mb-2 border-[#F0F2F5]"
              >
                <div className={`mt-2 mb-2 relative rounded-lg`}
                  style={{
                    paddingLeft: bulkEditGroupId === tg.id ? "9%" : "6%",
                  }}>
                  {/* Vertical line connecting task to subtasks */}

                  <table
                    className="w-full bg-white"
                    style={{
                      borderLeft: "4px solid #3B0069",
                      borderRadius: "0.5rem",

                      // marginLeft: "1rem",
                    }}
                  >
                    <tbody className="">
                      {task.subtasks.map((st, sidx) => (
                        <SubtaskRow
                          key={st.id}
                          tg={tg}
                          task={task}
                          st={st}
                          isOdd={sidx % 2 === 1}
                        />
                      ))}
                      {task.subtasks.length === 0 && (
                        <tr className="border-b-[1px] border-b-[#78797A]">
                          <td
                            colSpan={6}
                            className="px-2 py-2 text-sm text-gray-700 text-left"
                          >
                            No Subtasks
                          </td>
                        </tr>
                      )}
                      {taskAuthority && (
                        <tr className="border-b-[1px] border-b-[#78797A]">
                          <td className="px-0 py-2 text-left text-sm text-gray-700 border-l-4 border-l-[#3B006966] ">
                            {showAddSubtaskModal === task.id ? (
                              <form
                                onSubmit={async (e) => {
                                  e.preventDefault();
                                  const subtaskData = {
                                    subtask_name: e.target[0].value,
                                    task_id: task.id,
                                  };
                                  await handleAddSubtask(subtaskData);
                                }}
                                className="flex w-full px-2 items-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  autoFocus
                                  type="text"
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      e.preventDefault();
                                      setShowAddSubtaskModal(null);
                                    }
                                  }}
                                  placeholder="New Subtask Name"
                                  maxLength={45}
                                  className="pl-2 py-1 w-full text-[16px] mr-2 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500"
                                />
                                <button
                                  type="submit"
                                  className="text-purple-600 hover:text-purple-800"
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setShowAddSubtaskModal(null)}
                                  className="text-gray-500 hover:text-gray-700 ml-2"
                                >
                                  ✕
                                </button>
                              </form>
                            ) : (
                              <button
                                onClick={() => {
                                  onAddSubtaskClick(tg.id, task.id, task.dueDate);
                                  setShowAddTaskModal(null); // Close any open task modal
                                }}
                                className="text-purple-600 px-3 hover:text-purple-800 flex items-center"
                              >
                                ➕ Add Subtask
                              </button>
                            )}
                          </td>
                          <td colSpan={5}></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          )
        }
      </>
    )
  };

  /* -------- GROUP BLOCK -------- */
  const GroupBlock = ({ tg }) => {
    const Ongoing = tg.statusPercentages?.inProgress || 0;
    const Complete = tg.statusPercentages?.completed || 0;
    const Overdue = tg.statusPercentages?.overdue || 0;
    const NotStarted = tg.statusPercentages?.notStarted || 0;
    const High = tg.priorityPercentages?.high || 0;
    const Medium = tg.priorityPercentages?.medium || 0;
    const Low = tg.priorityPercentages?.low || 0;

    // Ref for the group container – helps us keep the toggled group in view
    const groupRef = useRef(null);

    // Helper to toggle and scroll the group into view
    const handleToggleGroup = (e) => {
      onToggleGroup(e, tg.id);
    };

    return (
      <div className="mb-2">
        {/* header row */}
        <div
          ref={groupRef}
          className={`
            flex overflow-visible cursor-pointer rounded-lg group
            ${tg.expanded ? "bg-gray-100" : " hover:bg-slate-300 bg-white"} `}
          style={{
            borderLeftColor: tg.expanded ? "transparent" : "#3B0069",
            borderTopWidth: tg.expanded ? "0px" : "1px",
            borderRightWidth: tg.expanded ? "0px" : "1px",
            borderBottomWidth: tg.expanded ? "0px" : "1px",
            borderTopColor: tg.expanded ? "transparent" : "#78797A",
            borderRightColor: tg.expanded ? "transparent" : "#78797A",
            borderBottomColor: tg.expanded ? "transparent" : "#78797A",
            borderLeftWidth: tg.expanded ? "0px" : "4px",
          }}
          onClick={() => {
            setSelectedTaskGroup(tg);
            setSelectedTask(null);
          }}
        >
          <div className="flex-grow px-4 py-2 hover:bg-[#ECE9EE]">
            <div className="grid grid-cols-12 gap-1 items-center">
              {/* name + inline-edit */}
              <div
                className="flex flex-row items-center col-span-3"
                onClick={() => {
                  setSelectedTaskGroup(tg);
                  setSelectedTask(null);
                }}
              >
                <button
                  className="mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleGroup(e);
                  }}
                >
                  <svg
                    className={clsx("w-5 h-5 transform", {
                      "rotate-90": tg.expanded,
                    })}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M9 5l7 7-7 7"
                      strokeWidth={2}
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <div
                  className="flex flex-col w-[80%] items-start justify-between "
                >
                  {editingGroupId === tg.id ? (
                    <form
                      onSubmit={onGroupEditSubmit}
                      className="flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        autoFocus
                        value={editingGroupName}
                        onChange={(e) =>
                          onGroupEditStart({ ...tg, name: e.target.value })
                        }
                        onBlur={onGroupEditSubmit}
                        className="pl-2 text-[18px] mr-2 border border-purple-300 rounded focus:ring-purple-500"
                      />
                      <button
                        type="button"
                        onClick={onGroupEditCancel}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </form>
                  ) : (
                    <span
                      className={`pl-2 truncate w-full text-[18px] px-2 font-medium ${taskAuthority
                        ? "hover:border-[1px] hover:border-purple-600"
                        : ""
                        }  rounded-lg cursor-pointer hover:text-purple-600`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (taskAuthority) {
                          onGroupEditStart(tg);
                        } else {
                          setSelectedTaskGroup(tg);
                          setSelectedTask(null);
                        }
                      }}
                    >
                      {tg.name}
                    </span>
                  )}
                  {/* quick ratio */}
                  {tg.tasks.length > 0 && tg.expanded && (
                    <span className="ml-2 col-span-2 text-xs text-gray-500 flex items-center">
                      {tg.tasks.filter((t) => t.status === "Complete").length}/
                      {tg.tasks.length} tasks complete
                    </span>
                  )}
                </div>
              </div>


              {/* group members avatars */}
              {Array.isArray(tg.allocated_members) && (
                <div className="flex items-center col-span-2">
                  {tg.allocated_members.slice(0, 3).map((a, index) => {
                    const siteMember =
                      siteMembers.find(
                        (sm) =>
                          sm.member_id === (a?.member_id ?? a?.id)
                      ) || {};
                    const avatarUrl = siteMember.userpic || a?.userpic;
                    const firstName = a?.first_name || siteMember.first_name || "";
                    const lastName = a?.last_name || siteMember.last_name || "";
                    return avatarUrl ? (
                      <img
                        key={a.member_id || index}
                        src={avatarUrl}
                        alt={`${firstName} ${lastName}`}
                        className="w-8 h-8 rounded-full border-2 border-white"
                        style={{ marginLeft: index > 0 ? "-6px" : "0px" }}
                        title={`${firstName} ${lastName}`}
                      />
                    ) : (
                      <span
                        key={a.member_id || index}
                        className="w-8 h-8 rounded-full bg-[#343C6A] text-white font-medium text-xs flex items-center justify-center border-2 border-white"
                        style={{ marginLeft: index > 0 ? "-6px" : "0px" }}
                        title={`${firstName} ${lastName}`}
                      >
                        {getInitials(
                          `${firstName} ${lastName}`
                        )}
                      </span>
                    );
                  })}
                  {tg.allocated_members.length > 3 && (
                    <span
                      className="w-8 h-8 rounded-full bg-[#343C6A] text-white text-xs flex items-center justify-center border-2 border-white"
                      style={{ marginLeft: "-6px" }}
                      title={`+${tg.allocated_members.length - 3
                        } more members`}
                    >
                      +{tg.allocated_members.length - 3}
                    </span>
                  )}
                </div>
              )}
              {tg.expanded && taskAuthority && (
                <div className="flex flex-row col-span-7 items-center justify-end">


                  {bulkEditGroupId === tg.id && (
                    <div
                      className="ml-1 flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative flex items-center gap-2">
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowBulkAssigneeDropdown(!showBulkAssigneeDropdown);
                          }}
                          className="py-1 px-2 bg-white rounded-lg border text-sm text-gray-600">
                          Assignees {bulkMembers.length ? `(${bulkMembers.length})` : ""}
                        </div>
                        {/* <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowBulkAssigneeDropdown(!showBulkAssigneeDropdown);
                          }}
                          className="border rounded px-2 py-1 text-sm bg-white hover:bg-gray-50"
                        >
                          {bulkMembers.length ? `${bulkMembers.length} selected` : "Choose"}
                        </button> */}
                        {showBulkAssigneeDropdown && (
                          <div
                            className="absolute top-full left-0 mt-1 z-30 bg-white rounded-lg shadow-lg border p-2 w-72 max-h-60 overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {siteMembers.map((member) => (
                              <div
                                key={member.member_id}
                                className={`flex items-center justify-between px-2 py-1 rounded cursor-pointer hover:bg-gray-100 ${bulkMembers.includes(member.member_id) ? "bg-gray-50" : ""}`}
                                onClick={() =>
                                  toggleBulkMemberSelection(Number(member.member_id))
                                }
                              >
                                <div className="flex items-center">
                                  {member?.userpic ? (
                                    <img
                                      src={member.userpic}
                                      alt={`${member.first_name} ${member.last_name}`}
                                      className="w-6 h-6 rounded-full border-2 border-white mr-2"
                                    />
                                  ) : (
                                    <span
                                      className="w-6 h-6 rounded-full bg-[#343C6A] text-white text-xs flex items-center justify-center mr-2"
                                      title={`${member.first_name} ${member.last_name}`}
                                    >
                                      {getInitials(`${member.first_name} ${member.last_name}`)}
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-700">
                                    {member.first_name} {member.last_name}
                                  </span>
                                </div>
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                  checked={!!bulkMembers.includes(member.member_id)}
                                  onChange={() =>
                                    toggleBulkMemberSelection(Number(member.member_id))
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {/* <span className="text-sm text-gray-600">
                          Priority
                        </span> */}
                        <select
                          value={bulkPriority}
                          onChange={(e) => setBulkPriority(e.target.value)}
                          className="border rounded px-2 py-1 text-sm bg-white"
                        >
                          <option value="">Select Priority</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>

                      {/* <label className="flex items-center gap-2 ml-2">
                        <input
                          type="checkbox"
                          checked={bulkDelete}
                          onChange={(e) => setBulkDelete(e.target.checked)}
                        />
                        <span className="text-sm text-red-600">
                          Delete selected
                        </span>
                      </label> */}

                      <div className="flex items-center gap-2 ml-2">
                        <button
                          disabled={bulkSaving}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBulkSave(tg);
                          }}
                          className={`px-3 py-1 rounded text-white ${bulkSaving ? "bg-gray-400" : "bg-btn-bg hover:bg-purple-800"} text-sm`}
                        >
                          {bulkSaving ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTaskIds([]);
                            setBulkMembers([]);
                            setBulkPriority("");
                            setBulkDelete(false);
                          }}
                          className="px-3 py-1 rounded border text-sm"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    className="ml-3 px-3 py-1 rounded-lg text-sm border hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (bulkEditGroupId === tg.id) {
                        setBulkEditGroupId(null);
                        setSelectedTaskIds([]);
                        setBulkMembers([]);
                        setBulkPriority("");
                        setBulkDelete(false);
                      } else {
                        setBulkEditGroupId(tg.id);
                        setSelectedTaskIds([]);
                      }
                    }}
                    title="Select tasks for bulk actions"
                  >
                    {bulkEditGroupId === tg.id ? "Cancel Select" : "Select"}
                  </button>
                </div>
              )}
              {/* status bar – hidden when expanded */}
              {!tg.expanded && (
                <>
                  <div className="col-span-2 flex flex-col items-center">
                    <span className="text-xs text-gray-500">Status</span>
                    <div className="flex h-4 w-[70%] overflow-hidden rounded-full border mt-1">
                      <div
                        className="bg-yellow-400"
                        style={{ width: `${Ongoing}%` }}
                        title={`Ongoing tasks: ${Ongoing}%`}
                      />
                      <div
                        className="bg-green-500"
                        style={{ width: `${Complete}%` }}
                        title={`Completed tasks: ${Complete}%`}
                      />
                      <div
                        className="bg-red-500"
                        style={{ width: `${Overdue}%` }}
                        title={`Overdue tasks: ${Overdue}%`}
                      />
                      <div
                        className="bg-gray-400"
                        style={{ width: `${NotStarted}%` }}
                        title={`Not started tasks: ${NotStarted}%`}
                      />
                    </div>
                  </div>

                  <div className="col-span-2 text-center">
                    <span className="text-xs text-gray-500 block">
                      Due Date
                    </span>
                    {tg.dueDate || "Not set"}
                  </div>

                  <div className="col-span-2 flex flex-col items-center">
                    <span className="text-xs text-gray-500">Priority</span>
                    <div className="flex h-4 w-[70%] overflow-hidden rounded-full border mt-1">
                      <div
                        className="bg-[#C0B3F3]"
                        title={`Low priority tasks: ${Low}%`}
                        style={{ width: `${Low}%` }}
                      />
                      <div
                        className="bg-[#8067E0]"
                        style={{ width: `${Medium}%` }}
                        title={`Medium priority tasks: ${Medium}%`}
                      />
                      <div
                        className="bg-[#2E1B76]"
                        style={{ width: `${High}%` }}
                        title={`High priority tasks: ${High}%`}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* expanded task table */}
        {tg.expanded && (
          <div className="mt-1 pl-4 pr-4 py-1 overflow-visible">
            <div
              className="rounded-xl bg-white  overflow-visible"
              style={{ borderColor: tg.color || "#3B0069" }}
            >
              {/* Always show the table header and Add Task button when expanded */}
              <table className="w-full border-none border-0 overflow-visible">
                <thead>
                  <tr
                    className="text-black font-normal"
                    style={{
                      borderLeftWidth: "6px",
                      borderLeftColor: "#3B0069",
                      borderBottom: "1px solid #78797A",
                      borderTop: "1px solid #78797A",
                      borderRight: "1px solid #78797A",
                    }}
                  >
                    {bulkEditGroupId === tg.id && (
                      <th className="py-3 px-0 w-[20px] text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[#3B0069]"
                          checked={
                            tg.tasks.length > 0 &&
                            selectedTaskIds.length === tg.tasks.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTaskIds(tg.tasks.map((t) => t.id));
                            } else {
                              setSelectedTaskIds([]);
                            }
                          }}
                        />
                      </th>
                    )}
                    <th className="py-3 px-4 w-[240px] text-center font-normal">
                      Tasks
                    </th>
                    <th className="py-3 px-4 font-normal text-center w-[200px]">
                      Assignee
                    </th>
                    <th className="py-3 px-4 font-normal text-center">
                      Due Date
                    </th>
                    <th className="py-3 px-4 font-normal text-center w-[140px]">
                      Status
                    </th>
                    <th className="py-3 px-4 font-normal text-center w-[120px]">
                      Priority
                    </th>
                    <th className="py-3 px-4 font-normal text-center w-[80px]">
                      Duration
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Bulk action controls are now next to the group 'Cancel Select' button */}
                  {/* Show existing tasks if any */}
                  {tg.tasks.map((task, idx) => (
                    <TaskBlock key={task.id} tg={tg} task={task} idx={idx} />
                  ))}
                  {tg.tasks.length === 0 && !taskAuthority && (
                    <tr className="">
                      <td
                        colSpan={bulkEditGroupId === tg.id ? 7 : 6}
                        className="px-3 py-2 text-base text-gray-700 text-left"
                      >
                        No Tasks
                      </td>
                    </tr>
                  )}

                  {/* Always show "Add Task" row when group is expanded */}
                  {(currentUserMember?.is_admin ||
                    currentUserMember?.create_taskgroup ||
                    currentUserMember?.joined_as === "owner") && bulkEditGroupId !== tg.id && (
                      <tr className="bg-gray-50 border-l-[6px] border-[#3c006976]">
                        <td className="px-4 py-3 text-left text-sm">
                          {showAddTaskModal === tg.id ? (
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                const taskData = {
                                  name: taskInputValue,
                                  tgId: tg.id,
                                };
                                handleAddTask(taskData);
                                setTaskInputValue(""); // Clear input after submission
                              }}
                              className="flex items-center relative"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="relative flex-1">
                                <input
                                  autoFocus
                                  type="text"
                                  placeholder="New Task Name"
                                  maxLength={45}
                                  value={taskInputValue}
                                  onChange={(e) =>
                                    setTaskInputValue(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      setShowAddTaskModal(null);
                                      setTaskInputValue("");
                                    }
                                  }}
                                  className="pl-2 py-1 text-[16px] mr-2 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 transition-all duration-200 ease-in-out"
                                  style={{
                                    minWidth: "200px",
                                  }}
                                />
                                {taskInputValue.length >= 35 && (
                                  <div className="absolute -bottom-6 left-0 text-xs text-gray-500">
                                    {taskInputValue.length}/45 characters
                                  </div>
                                )}
                              </div>
                              <button
                                type="submit"
                                disabled={!taskInputValue.trim()}
                                className={`text-purple-600 hover:text-purple-800 ${!taskInputValue.trim()
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                                  }`}
                              >
                                Add
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAddTaskModal(null);
                                  setTaskInputValue(""); // Clear input when canceling
                                }}
                                className="text-gray-500 hover:text-gray-700 ml-2"
                              >
                                ✕
                              </button>
                            </form>
                          ) : (
                            <button
                              onClick={() => {
                                setShowAddTaskModal(tg.id);
                                setShowAddSubtaskModal(null);
                              }}
                              className="text-purple-600 hover:text-purple-800 flex items-center"
                            >
                              ➕ Add Task
                            </button>
                          )}
                        </td>
                        <td colSpan={bulkEditGroupId === tg.id ? 6 : 5}></td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ───────────────────────── render ───────────────────────── */
  return (
    <>
      {/* top bar */}
      <div className="py-2 flex flex-row justify-between px-5 items-center">
        <span className="text-xl font-medium">Task Groups</span>

        {/* Search and Filters */}
        {/* <div className="flex items-center space-x-4 flex-1 max-w-md mx-4">

        </div> */}

        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <FilterButton />
          {/* refresh button */}
          <button
            onClick={onRefresh}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded px-4 py-2 text-sm flex items-center"
            disabled={refreshing}
          >
            <FaSyncAlt className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>

          {(isOwner || taskAuthority) && (
            <button
              onClick={onAddTaskGroupClick}
              className="bg-btn-bg hover:bg-purple-800 text-white rounded px-4 py-2 text-sm flex items-center"
            >
              <span className="mr-1 font-bold">+</span> Add Task Group
            </button>
          )}
        </div>
      </div>

      {/* all groups */}
      <div className="px-4 py-1">
        {/* Show filter info if active */}
        {(searchTerm || statusFilter !== "all" || priorityFilter !== "all") && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-sm text-blue-800">
                {searchTerm && `Searching for "${searchTerm}" `}
                {statusFilter !== "all" && `Status: ${statusFilter} `}
                {priorityFilter !== "all" && `Priority: ${priorityFilter}`}
                {` • ${filterTaskGroups(taskGroups).reduce(
                  (sum, group) => sum + group.tasks.length,
                  0
                )} tasks found`}
              </div>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setPriorityFilter("all");
                }}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {(() => {
          const filteredGroups = filterTaskGroups(taskGroups);
          return filteredGroups.length > 0 ? (
            filteredGroups.map((tg) => <GroupBlock key={tg.id} tg={tg} />)
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <img
                src={TaskEmptyIcon}
                alt="No tasks"
                className="mb-4"
                width={300}
              />
              <span className="text-gray-500 text-2xl">
                {searchTerm ||
                  statusFilter !== "all" ||
                  priorityFilter !== "all"
                  ? "No tasks match your filters"
                  : "No Task Groups"}
              </span>
              {(searchTerm ||
                statusFilter !== "all" ||
                priorityFilter !== "all") && (
                  <span className="text-gray-500 text-sm mt-2">
                    Try adjusting your search or filter criteria
                  </span>
                )}
              {!searchTerm &&
                statusFilter === "all" &&
                priorityFilter === "all" && (
                  <span className="text-gray-500 text-sm">
                    Add a Task Group to organize your tasks
                  </span>
                )}
            </div>
          );
        })()}
      </div>
    </>
  );
};

export default MainTableView;